import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type PointerEvent,
} from "react";
import type { TooltipContent } from "@/components/ui/tooltip";

function useTooltipOpen() {
  const [open, setOpen] = useState(false);
  const hoverTimer = useRef<number | undefined>(undefined);
  const clearHoverTimer = useCallback(() => {
    window.clearTimeout(hoverTimer.current);
    hoverTimer.current = undefined;
  }, []);
  const onOpenChange = useCallback(
    (value: boolean) => {
      clearHoverTimer();
      setOpen(value);
    },
    [clearHoverTimer]
  );
  useEffect(() => {
    const dismiss = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) onOpenChange(false);
    };
    document.addEventListener("keydown", dismiss);
    return () => {
      clearHoverTimer();
      document.removeEventListener("keydown", dismiss);
    };
  }, [clearHoverTimer, onOpenChange]);
  const onPointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    // Own the delay so every dismissal can cancel a queued hover opening.
    event.preventDefault();
    if (event.pointerType !== "touch" && !open && hoverTimer.current === undefined) {
      hoverTimer.current = window.setTimeout(() => onOpenChange(true), 150);
    }
  };
  const toggleOpen = () => {
    clearHoverTimer();
    setOpen((wasOpen) => !wasOpen);
  };
  return { open, onOpenChange, onPointerMove, clearHoverTimer, toggleOpen };
}

export function useBillingPlanTooltip() {
  const { open, onOpenChange, onPointerMove, clearHoverTimer, toggleOpen } = useTooltipOpen();
  const touchPointer = useRef(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const onPointerDownOutside: NonNullable<
    ComponentProps<typeof TooltipContent>["onPointerDownOutside"]
  > = (event) => {
    const target = event.detail.originalEvent.target;
    if (target instanceof Node && triggerRef.current?.contains(target)) event.preventDefault();
  };
  const triggerProps: ComponentProps<"button"> = {
    ref: triggerRef,
    onPointerMove,
    onPointerLeave: clearHoverTimer,
    onFocus: clearHoverTimer,
    onBlur: clearHoverTimer,
    onPointerDown: (event) => {
      event.preventDefault();
      clearHoverTimer();
      touchPointer.current = event.pointerType === "touch";
    },
    onPointerUp: (event) => {
      // Touch release also works when WebKit omits the compatibility click.
      if (event.pointerType === "touch") toggleOpen();
    },
    onPointerCancel: () => {
      touchPointer.current = false;
    },
    onClick: (event) => {
      event.preventDefault();
      if (!touchPointer.current || event.detail === 0) onOpenChange(true);
      touchPointer.current = false;
    },
  };
  return { open, onOpenChange, triggerProps, onPointerDownOutside };
}
